/**
 * "O que fazer agora": ate cinco cartoes acionaveis derivados dos numeros da
 * janela (agregacao.ts, `gerarInsights`).
 *
 * Fica ENTRE o caminho e a regua: o caminho diz o que aconteceu, a regua diz
 * como; este bloco diz onde o olho deve parar e o que fazer a respeito. Cada
 * cartao leva a um lugar DESTA pagina (recortar por oferta, abrir a aba de
 * leads ja filtrada, abrir a configuracao do toque) -- nunca a outra tela.
 *
 * O tom e texto + icone + cor, nunca so cor. A lista vazia nao existe: quando
 * nao ha nada urgente o proprio gerador devolve um cartao "nada urgente" que
 * ensina o que faz um aviso aparecer.
 */

import { Badge, Button, Footnote, Icon, SectionCard, type BadgeTone, type IconName } from '../../../components/ui';
import type { AcaoDoInsight, FunnelKind, Insight, TomDoInsight } from '../types';

const TOM: Readonly<Record<TomDoInsight, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName; readonly borda: string }>> = {
  alerta: { label: 'Atenção', tone: 'warning', icon: 'alerta', borda: 'border-status-warning/50' },
  oportunidade: { label: 'Oportunidade', tone: 'good', icon: 'raio', borda: 'border-status-good/40' },
  projecao: { label: 'Projeção', tone: 'info', icon: 'grafico', borda: 'border-info/40' },
  bom: { label: 'Tudo em ordem', tone: 'good', icon: 'check', borda: 'border-line' },
};

export function OQueFazerAgora({
  kind,
  insights,
  onAcao,
}: {
  readonly kind: FunnelKind;
  readonly insights: readonly Insight[];
  readonly onAcao: (acao: AcaoDoInsight) => void;
}): JSX.Element {
  const alertas = insights.filter((i) => i.tom === 'alerta').length;
  const oportunidades = insights.filter((i) => i.tom === 'oportunidade').length;

  return (
    <SectionCard
      id="o-que-fazer-agora"
      title="O que fazer agora"
      icon="raio"
      description="Lido direto dos números desta janela. Cada cartão leva ao lugar certo da própria página."
      aside={
        <>
          {alertas > 0 ? (
            <Badge tone="warning" dot>
              {alertas} {alertas === 1 ? 'atenção' : 'atenções'}
            </Badge>
          ) : null}
          {oportunidades > 0 ? (
            <Badge tone="good" dot>
              {oportunidades} {oportunidades === 1 ? 'oportunidade' : 'oportunidades'}
            </Badge>
          ) : null}
        </>
      }
    >
      {insights.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge px-4 py-6 text-center text-sm text-ink-subtle">
          Ainda não há entradas nesta janela. Os avisos nascem de quem {kind === 'expirados' ? 'venceu' : 'abandonou'} e do que a régua fez com essas pessoas.
        </p>
      ) : (
        <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {insights.map((i, indice) => {
            const t = TOM[i.tom];
            return (
              <li key={i.key} className={`flex h-full flex-col gap-2 rounded-xl border bg-elevated px-4 py-3 ${t.borda} ${indice === 0 && insights.length > 1 ? 'md:col-span-2 xl:col-span-1' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <Badge tone={t.tone} icon={t.icon}>
                    {t.label}
                  </Badge>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{indice + 1}º</span>
                </div>
                <h3 className="text-sm font-semibold leading-snug text-ink">{i.titulo}</h3>
                <p className="flex-1 text-xs leading-relaxed text-ink-muted">{i.texto}</p>
                {i.acao !== null && i.rotuloAcao !== null ? (
                  <div>
                    <Button variant="secondary" size="sm" iconRight="seta-dir" onClick={() => (i.acao === null ? undefined : onAcao(i.acao))}>
                      {i.rotuloAcao}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3">
        <Footnote>
          <span className="inline-flex items-center gap-1">
            <Icon name="escudo" size="xs" />
            Nenhum aviso sugere falar com quem ainda não saiu: a régua só toca em quem {kind === 'expirados' ? 'já venceu' : 'já abandonou'}. É isso que sustenta a comissão.
          </span>
        </Footnote>
      </div>
    </SectionCard>
  );
}
